import React from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../../components/ui/Card/Card';
import Input from '../../components/ui/Input/Input';
import Select from '../../components/ui/Select/Select';
import MultiSelect from '../../components/ui/MultiSelect/MultiSelect';
import Button from '../../components/ui/Button/Button';
import axiosInstance from '../../../Components/Request';
import { useAuth } from '../../../Components/AuthContext';
import hasPermission from '../../../Hooks/hasPermission';
import { useLayout } from '../../layout/LayoutContext';
import PermissionsTree from '../UserDetail/components/PermissionsTree';
import type { PermissionNode, RolePermissions } from '../UserDetail/useUserDetail';
import styles from './CreateUser.module.css';

const emailOk = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

const descendantsOf = (perms: PermissionNode[], id: string): string[] => {
  const map: Record<string, PermissionNode[]> = {};
  perms.forEach((p) => (map[p.id] = []));
  perms.forEach((p) => {
    if (p.father_id && map[p.father_id]) map[p.father_id].push(p);
  });
  const walk = (pid: string): string[] => {
    let acc: string[] = [];
    (map[pid] || []).forEach((c) => {
      acc.push(c.id);
      acc = acc.concat(walk(c.id));
    });
    return acc;
  };
  return walk(id);
};

const CreateUser: React.FC = () => {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const perms = React.useMemo(() => {
    const p = authUser?.permissionsActives;
    return {
      canCreate: hasPermission(p, 'US_CREATE'),
      canViewAdmin: hasPermission(p, 'US_AD_VIEW'),
      canViewClients: hasPermission(p, 'CL_VIEW'),
    };
  }, [authUser]);

  const { setHideHeader } = useLayout();
  React.useEffect(() => {
    setHideHeader(true);
    return () => setHideHeader(false);
  }, [setHideHeader]);

  const [roles, setRoles] = React.useState<RolePermissions[]>([]);
  const [clients, setClients] = React.useState<{ id: string; name: string }[]>([]);

  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [selectedClients, setSelectedClients] = React.useState<string[]>([]);
  const [roleId, setRoleId] = React.useState('');
  const [selectedPerms, setSelectedPerms] = React.useState<string[]>([]);

  const [touched, setTouched] = React.useState<Record<string, boolean>>({});
  const mark = (k: string) => setTouched((t) => ({ ...t, [k]: true }));
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (perms.canViewAdmin) axiosInstance.get('/permissions-list').then((r) => setRoles(r.data || [])).catch(() => {});
    if (perms.canViewClients) axiosInstance.get('/clients').then((r) => setClients(r.data || [])).catch(() => {});
  }, [perms.canViewAdmin, perms.canViewClients]);

  const optionPermissions = React.useMemo<PermissionNode[]>(
    () => roles.find((r) => r.role_id === roleId)?.permissions || [],
    [roles, roleId]
  );

  const changeRole = (v: string) => {
    setRoleId(v);
    const rolePerms = roles.find((r) => r.role_id === v)?.permissions || [];
    // Autoseleccionar LOADS + descendientes (patrón V1)
    const loads = rolePerms.find((p) => p.name === 'LOADS' && !p.father_id);
    setSelectedPerms(loads ? [loads.id, ...descendantsOf(rolePerms, loads.id)] : []);
  };

  const nameErr = touched.name && !name.trim();
  const emailErr = touched.email && !emailOk(email);
  const pwErr = touched.password && password.length < 6;
  const clientsErr = touched.clients && selectedClients.length === 0;
  const invalid = !name.trim() || !emailOk(email) || password.length < 6 || selectedClients.length === 0 || !roleId || selectedPerms.length === 0;

  const submit = async () => {
    if (invalid || submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await axiosInstance.post('/auth/register', {
        name: name.trim(),
        email: email.trim(),
        password,
        clients: selectedClients,
        role: roleId,
        permissions: selectedPerms,
      });
      const id = res.data?.user?.id;
      navigate(id ? `/users/${id}` : '/users');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'An error occurred while creating the user');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`${styles.page} dylo-animate-in`}>
      <div className={styles.topGrid}>
        {/* ===== Datos ===== */}
        <Card className={submitting ? 'dylo-loading-host' : undefined}>
          {submitting && <span className="dylo-loading-sweep" />}
          <h2 className={styles.cardTitle}>Create User</h2>

          <div className={styles.detailGrid}>
            <div className={styles.field}>
              <span className={styles.label}>Name</span>
              <Input value={name} invalid={nameErr} onChange={(e) => setName(e.target.value)} onBlur={() => mark('name')} />
            </div>
            <div className={styles.field}>
              <span className={styles.label}>Email</span>
              <Input value={email} invalid={emailErr} onChange={(e) => setEmail(e.target.value)} onBlur={() => mark('email')} />
            </div>
            <div className={styles.field}>
              <span className={styles.label}>Password</span>
              <Input
                type="password"
                value={password}
                invalid={pwErr}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={() => mark('password')}
                placeholder="Min. 6 characters"
              />
            </div>
            {perms.canViewAdmin && (
              <div className={styles.field}>
                <span className={styles.label}>Role</span>
                <Select
                  options={roles.map((r) => ({ label: r.role_name, value: r.role_id }))}
                  value={roleId}
                  searchable={false}
                  placeholder="Select a role"
                  onChange={(v) => changeRole((v as string) || '')}
                />
              </div>
            )}
          </div>

          {perms.canViewClients && (
            <div className={styles.clientField} onBlur={() => mark('clients')}>
              <MultiSelect
                label="Clients"
                options={clients.map((c) => ({ label: c.name, value: c.id }))}
                selected={selectedClients}
                onChange={setSelectedClients}
                placeholder="Select clients"
              />
              {clientsErr && <span className={styles.fieldErr}>Select at least one client</span>}
            </div>
          )}

          {error && <div className={styles.alert}>{error}</div>}

          <Button
            variant="primary"
            fullWidth
            className={styles.createBtn}
            loading={submitting}
            disabled={invalid}
            onClick={submit}
          >
            Create User
          </Button>
        </Card>

        {/* ===== Permisos ===== */}
        {perms.canViewAdmin && (
          <Card>
            <h2 className={styles.cardTitle}>Permissions</h2>
            {!roleId ? (
              <p className={styles.permHint}>Select a role to configure permissions.</p>
            ) : (
              <PermissionsTree permissions={optionPermissions} value={selectedPerms} onChange={setSelectedPerms} />
            )}
          </Card>
        )}
      </div>
    </div>
  );
};

export default CreateUser;
